import { API, graphqlOperation } from 'aws-amplify';
import { 
  listRoleDefinitions,
  getRoleDefinition 
} from 'graphql/queries';
import { 
  createRoleDefinition as createRoleDefinitionMutation,
  updateRoleDefinition as updateRoleDefinitionMutation
} from 'graphql/mutations';
import * as logger from 'utils/logger';

/**
 * Maps OrgLevel enum values to numeric levels for sorting
 */
const orgLevelToNumber = (orgLevel) => {
  switch (orgLevel) {
    case 'LOCATION_LEVEL': return 0;
    case 'ORGLEVEL_1': return 1;
    case 'ORGLEVEL_2': return 2;
    case 'ORGLEVEL_3': return 3;
    default: return 999;
  }
};

/**
 * Fetches all role definitions for a client
 * @param {string} clientId - The client identifier to filter role definitions
 * @returns {Promise<Array>} Array of role definitions sorted by assignmentLevel and sortOrder
 * @throws {Error} If the GraphQL query fails
 */
export const fetchRoleDefinitions = async (clientId) => {
  // Parameter validation
  if (!clientId || typeof clientId !== 'string') {
    throw new Error('Valid clientId is required');
  }

  try {
    const filter = {
      clientId: { eq: clientId }
    };
    
    // Use graphqlOperation with error policy to handle partial data
    const result = await API.graphql({
      ...graphqlOperation(listRoleDefinitions, { 
        filter,
        limit: 100 // Role definitions are typically limited
      }),
      // Continue even if some items have errors
      errorPolicy: 'all'
    });
    
    // Return empty array if no items found (don't throw error)
    if (!result?.data?.listRoleDefinitions?.items) {
      logger.info('No role definitions found for client', { clientId });
      return [];
    }
    
    // Filter out null items and items without assignmentLevel (old data)
    const items = (result.data.listRoleDefinitions.items || [])
      .filter(item => item !== null && item.assignmentLevel);
    
    if (items.length === 0) {
      logger.info('No valid role definitions found for client', { clientId });
      return [];
    }
    
    // Sort by assignmentLevel then by sortOrder
    return items.sort((a, b) => {
      const levelA = orgLevelToNumber(a.assignmentLevel);
      const levelB = orgLevelToNumber(b.assignmentLevel);
      
      if (levelA !== levelB) {
        return levelA - levelB;
      }
      return (a.sortOrder || 999) - (b.sortOrder || 999);
    });
  } catch (error) {
    logger.error('Failed to fetch role definitions', { clientId, error });
    // Return empty array instead of throwing for better UX
    return [];
  }
};

/**
 * Fetches a single role definition by ID
 * @param {string} id - The role definition ID
 * @returns {Promise<Object>} Role definition record
 * @throws {Error} If the GraphQL query fails
 */
export const fetchRoleDefinition = async (id) => {
  // Parameter validation
  if (!id || typeof id !== 'string') {
    throw new Error('Valid role definition ID is required');
  }

  try {
    const result = await API.graphql(
      graphqlOperation(getRoleDefinition, { id })
    );
    
    return result.data.getRoleDefinition;
  } catch (error) {
    logger.error('Failed to fetch role definition', { id, error });
    throw new Error('Unable to fetch role definition. Please try again.');
  }
};

/**
 * Creates a new role definition
 * @param {Object} roleData - Role data including roleCode, roleName, assignmentLevel, etc.
 * @param {string} createdBy - ID of the user creating the role
 * @returns {Promise<Object>} Created role definition record
 * @throws {Error} If the GraphQL mutation fails
 */
export const createRoleDefinition = async (roleData, createdBy) => {
  // Parameter validation
  if (!roleData || typeof roleData !== 'object') {
    throw new Error('Role data is required');
  }
  if (!roleData.roleCode || typeof roleData.roleCode !== 'string') {
    throw new Error('Valid roleCode is required');
  }
  if (!roleData.roleName || typeof roleData.roleName !== 'string') {
    throw new Error('Valid roleName is required');
  }
  if (!roleData.assignmentLevel) {
    throw new Error('Valid assignmentLevel is required');
  }
  if (!roleData.clientId || typeof roleData.clientId !== 'string') {
    throw new Error('Valid clientId is required');
  }
  if (!createdBy || typeof createdBy !== 'string') {
    throw new Error('Valid createdBy user ID is required');
  }

  try {
    const input = {
      ...roleData,
      isActive: roleData.isActive !== false,
      isDefault: roleData.isDefault === true,
      createdBy,
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };
    
    const result = await API.graphql(
      graphqlOperation(createRoleDefinitionMutation, { input })
    );
    
    return result.data.createRoleDefinition;
  } catch (error) {
    logger.error('Failed to create role definition', { 
      roleCode: roleData.roleCode, 
      clientId: roleData.clientId, 
      error 
    });
    throw new Error('Unable to create role definition. Please try again.');
  }
};

/**
 * Updates an existing role definition
 * @param {string} id - The role definition ID to update
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated role definition record
 * @throws {Error} If the GraphQL mutation fails
 */
export const updateRoleDefinition = async (id, updates) => {
  // Parameter validation
  if (!id || typeof id !== 'string') {
    throw new Error('Valid role definition ID is required');
  }
  if (!updates || typeof updates !== 'object') {
    throw new Error('Updates object is required');
  }

  try {
    const input = {
      id,
      ...updates,
      lastModified: new Date().toISOString()
    };
    
    console.log('roleDefinitionService - updateRoleDefinition mutation input:', input);
    
    const result = await API.graphql(
      graphqlOperation(updateRoleDefinitionMutation, { input })
    );
    
    console.log('roleDefinitionService - updateRoleDefinition mutation result:', result);
    
    return result.data.updateRoleDefinition;
  } catch (error) {
    logger.error('Failed to update role definition', { id, error });
    console.error('roleDefinitionService - GraphQL error:', error);
    throw new Error('Unable to update role definition. Please try again.');
  }
};

/**
 * Deactivates a role definition (soft delete)
 * @param {string} id - The role definition ID to deactivate
 * @returns {Promise<Object>} Updated role definition with isActive=false
 * @throws {Error} If the GraphQL mutation fails
 */
export const deactivateRoleDefinition = async (id) => {
  // Parameter validation
  if (!id || typeof id !== 'string') {
    throw new Error('Valid role definition ID is required');
  }

  try {
    const input = {
      id,
      isActive: false,
      lastModified: new Date().toISOString()
    };
    
    const result = await API.graphql(
      graphqlOperation(updateRoleDefinitionMutation, { input })
    );
    
    return result.data.updateRoleDefinition;
  } catch (error) {
    logger.error('Failed to deactivate role definition', { id, error });
    throw new Error('Unable to deactivate role definition. Please try again.');
  }
};

/**
 * Reactivates a previously deactivated role definition
 * @param {string} id - The role definition ID to reactivate
 * @returns {Promise<Object>} Updated role definition with isActive=true
 * @throws {Error} If the GraphQL mutation fails
 */
export const reactivateRoleDefinition = async (id) => {
  // Parameter validation
  if (!id || typeof id !== 'string') {
    throw new Error('Valid role definition ID is required');
  }

  try {
    const input = {
      id,
      isActive: true,
      lastModified: new Date().toISOString()
    };
    
    const result = await API.graphql(
      graphqlOperation(updateRoleDefinitionMutation, { input })
    );
    
    return result.data.updateRoleDefinition;
  } catch (error) {
    logger.error('Failed to reactivate role definition', { id, error });
    throw new Error('Unable to reactivate role definition. Please try again.');
  }
};

/**
 * Sets a role definition as the default for a client
 * @param {string} id - The role definition ID to set as default
 * @param {string} clientId - The client identifier
 * @returns {Promise<Object>} Updated role definition with isDefault=true
 * @throws {Error} If the GraphQL mutation fails
 */
export const setDefaultRoleDefinition = async (id, clientId) => {
  // Parameter validation
  if (!id || typeof id !== 'string') {
    throw new Error('Valid role definition ID is required');
  }
  if (!clientId || typeof clientId !== 'string') {
    throw new Error('Valid clientId is required');
  }

  try {
    // First, unset any existing defaults
    const existingRoles = await fetchRoleDefinitions(clientId);
    const currentDefault = existingRoles.find(r => r.isDefault);
    
    if (currentDefault && currentDefault.id !== id) {
      await updateRoleDefinition(currentDefault.id, { isDefault: false });
    }
    
    // Then set the new default
    const input = {
      id,
      isDefault: true,
      lastModified: new Date().toISOString()
    };
    
    const result = await API.graphql(
      graphqlOperation(updateRoleDefinitionMutation, { input })
    );
    
    return result.data.updateRoleDefinition;
  } catch (error) {
    logger.error('Failed to set default role definition', { id, clientId, error });
    throw new Error('Unable to set default role definition. Please try again.');
  }
};

/**
 * Checks if a role code is unique within a client
 * @param {string} roleCode - The role code to check
 * @param {string} clientId - The client identifier
 * @param {string|null} excludeId - Role definition ID to exclude from check (for updates)
 * @returns {Promise<boolean>} True if role code is unique, false otherwise
 */
export const checkRoleCodeUniqueness = async (roleCode, clientId, excludeId = null) => {
  // Parameter validation
  if (!roleCode || typeof roleCode !== 'string') {
    throw new Error('Valid roleCode is required');
  }
  if (!clientId || typeof clientId !== 'string') {
    throw new Error('Valid clientId is required');
  }
  if (excludeId !== null && typeof excludeId !== 'string') {
    throw new Error('excludeId must be a string or null');
  }

  try {
    const filter = {
      clientId: { eq: clientId },
      roleCode: { eq: roleCode }
    };
    
    const result = await API.graphql(
      graphqlOperation(listRoleDefinitions, { 
        filter,
        limit: 1
      })
    );
    
    const items = result.data.listRoleDefinitions.items;
    if (excludeId) {
      return items.length === 0 || (items.length === 1 && items[0].id === excludeId);
    }
    
    return items.length === 0;
  } catch (error) {
    logger.error('Failed to check role code uniqueness', { roleCode, clientId, error });
    return false;
  }
};